import React from 'react';
import { DashboardRFQ } from '../../hooks/dashboardMappers';
import { RfqStateTag, RfqProgress, timeAgo, formatDate } from './rfqDisplay';
import { Skeleton } from '../ui';

/**
 * Tiling view of the RFQ list — one card per RFQ, auto-fill grid so tiles
 * reflow from narrow laptops to wide screens. Depth from the 1px border only;
 * clicking a card continues in the wizard at the RFQ's current step.
 */
interface RfqCardGridProps {
    rows: DashboardRFQ[];
    loading?: boolean;
    onOpen: (id: string) => void;
    /** Rendered instead of the grid when there are no rows and not loading. */
    empty: React.ReactNode;
}

const GRID = 'grid gap-s5 [grid-template-columns:repeat(auto-fill,minmax(250px,1fr))]';

const plural = (n: number, word: string, pluralWord?: string) =>
    `${n} ${n === 1 ? word : pluralWord ?? `${word}s`}`;

const RfqCard: React.FC<{ row: DashboardRFQ; onOpen: (id: string) => void }> = ({ row, onOpen }) => (
    <button
        type="button"
        onClick={() => onOpen(row.id)}
        className="flex cursor-pointer flex-col gap-s3 rounded-card border border-line bg-surface p-s5 text-left transition-colors hover:border-line-strong hover:bg-surface-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-action"
    >
        <span className="flex items-start justify-between gap-s3">
            <span className={`min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-[13px] ${row.name ? 'font-semibold text-ink' : 'font-medium text-ink-muted'}`}>
                {row.name ?? 'Untitled project'}
            </span>
            <RfqStateTag row={row} />
        </span>
        <span className="font-mono text-[11px] text-ink-muted">
            {row.id}{row.owner ? ` · ${row.owner}` : ''}
        </span>
        <RfqProgress row={row} />
        <span className="flex items-center gap-s2 border-t border-line-divider pt-s3 text-meta text-ink-muted">
            <span>{plural(row.partsCount, 'part')}</span>
            <span aria-hidden>·</span>
            {row.repliesCount > 0 && (
                <>
                    <span className="text-action-deep">{plural(row.repliesCount, 'reply', 'replies')}</span>
                    <span aria-hidden>·</span>
                </>
            )}
            <span title={`Created ${formatDate(row.createdAtIso)}`}>Updated {timeAgo(row.updatedAtIso)}</span>
        </span>
    </button>
);

const RfqCardGrid: React.FC<RfqCardGridProps> = ({ rows, loading = false, onOpen, empty }) => {
    if (loading) {
        return (
            <div className={GRID} aria-hidden>
                {Array.from({ length: 8 }, (_, i) => (
                    <div key={i} className="flex h-[124px] flex-col gap-s3 rounded-card border border-line bg-surface p-s5">
                        <Skeleton width="62%" height={11} />
                        <Skeleton width="38%" height={9} />
                    </div>
                ))}
            </div>
        );
    }
    if (rows.length === 0) return <>{empty}</>;
    return (
        <div className={GRID} role="list" aria-label="RFQs">
            {rows.map((row) => (
                <RfqCard key={row.id} row={row} onOpen={onOpen} />
            ))}
        </div>
    );
};

export default RfqCardGrid;
